import { FormControl, TextField, Select, MenuItem } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Business, Person, Add, KeyboardReturn } from '@mui/icons-material';
import { User } from '../../models/User';
import { useConfig } from '../../utils/useConfig';

interface AddressFormProps {
  control: any;
  errors: any;
  currentUser?: User | null;
  setValue?: any;
  watch?: any;
}

const POLISH_VOIVODESHIPS = [
  'dolnośląskie',
  'kujawsko-pomorskie',
  'lubelskie',
  'lubuskie',
  'łódzkie',
  'małopolskie',
  'mazowieckie',
  'opolskie',
  'podkarpackie',
  'podlaskie',
  'pomorskie',
  'śląskie',
  'świętokrzyskie',
  'warmińsko-mazurskie',
  'wielkopolskie',
  'zachodniopomorskie',
];

const AddressForm = ({
  control,
  errors,
  currentUser,
  setValue,
  watch,
}: AddressFormProps) => {
  const config = useConfig();

  const [isCompany, setIsCompany] = useState(false);
  const [useManualAddress, setUseManualAddress] = useState(
    !currentUser || !currentUser.addresses?.length,
  );

  // Znajdź domyślny adres lub użyj pierwszego
  const defaultAddress =
    currentUser?.addresses?.find((addr) => addr.isDefault) ||
    currentUser?.addresses?.[0];

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    defaultAddress?.id || null,
  );

  const handleBuyerTypeChange = (type: 'individual' | 'company') => {
    setIsCompany(type === 'company');

    // Czyścimy pola firmowe przy zmianie na osobę prywatną
    if (type === 'individual' && setValue) {
      setValue('NIP', '');
      setValue('companyName', '');
      setValue('companyStreetAndNumber', '');
      setValue('companyPostalCode', '');
      setValue('companyCity', '');
    }
  };

  // Ustawienie ID adresu w formularzu zamówienia
  useEffect(() => {
    if (selectedAddressId && setValue && !useManualAddress) {
      setValue('addressId', selectedAddressId);
      // Czyścimy pola adresu, bo będziemy używać adresu z ID
      setValue('fullName', undefined);
      setValue('street', undefined);
      setValue('buildingNumber', undefined);
      setValue('apartmentNumber', undefined);
      setValue('postalCode', undefined);
      setValue('city', undefined);
      setValue('province', undefined);
      setValue('phone', undefined);
      setValue('email', undefined);
    } else if (setValue) {
      setValue('addressId', undefined);
    }
  }, [selectedAddressId, setValue, useManualAddress]);

  const handleAddressSelection = (addressId: string) => {
    setSelectedAddressId(addressId);
    setUseManualAddress(false);
  };

  const handleManualAddressToggle = () => {
    setUseManualAddress(true);
    // Czyścimy ID adresu, bo będziemy używać ręcznie wprowadzonych pól
    if (setValue) {
      setValue('addressId', undefined);
    }
  };

  const handleReturnToSelection = () => {
    setUseManualAddress(false);

    // Przywracamy ID adresu jeśli był wcześniej wybrany
    if (selectedAddressId && setValue) {
      setValue('addressId', selectedAddressId);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <div className="p-5 space-y-6">
      <motion.div variants={itemVariants}>
        <p className="text-gray-700 font-medium mb-4">Kupujesz jako</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <motion.button
            type="button"
            onClick={() => handleBuyerTypeChange('individual')}
            className={`
              flex items-center p-4 border rounded-lg transition-all
              ${
                !isCompany
                  ? `bg-${config.theme.mainColor}-50 border-primary text-primary shadow-sm`
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }
            `}
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
          >
            <Person
              className={`mr-3 ${!isCompany ? 'text-primary' : 'text-gray-400'}`}
            />
            <span className="font-medium">Osoba prywatna</span>
          </motion.button>

          <motion.button
            type="button"
            onClick={() => handleBuyerTypeChange('company')}
            className={`
              flex items-center p-4 border rounded-lg transition-all
              ${
                isCompany
                  ? `bg-${config.theme.mainColor}-50 border-primary text-primary shadow-sm`
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }
            `}
            whileHover={{ y: -2 }}
            whileTap={{ y: 0 }}
          >
            <Business
              className={`mr-3 ${isCompany ? 'text-primary' : 'text-gray-400'}`}
            />
            <span className="font-medium">Firma</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Wybór zapisanego adresu dla zalogowanych użytkowników */}
      {currentUser &&
        currentUser.addresses &&
        currentUser.addresses.length > 0 && (
          <motion.div variants={itemVariants}>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-700 font-medium">Wybierz adres dostawy</p>
              <div>
                {useManualAddress ? (
                  <motion.button
                    type="button"
                    onClick={handleReturnToSelection}
                    className="px-4 py-2 flex items-center text-sm border border-gray-300 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-600"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <KeyboardReturn className="mr-1 h-4 w-4" />
                    <span>Powrót do wyboru adresu</span>
                  </motion.button>
                ) : (
                  <motion.button
                    type="button"
                    onClick={handleManualAddressToggle}
                    className={`px-4 py-2 flex items-center text-sm border border-primary bg-white hover:bg-${config.theme.mainColor}-50 rounded-lg text-primary`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Add className="mr-1 h-4 w-4" />
                    <span>Wprowadź nowy adres</span>
                  </motion.button>
                )}
              </div>
            </div>

            {!useManualAddress && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {currentUser.addresses.map((address) => (
                  <motion.div
                    key={address.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedAddressId === address.id
                        ? `border-primary bg-${config.theme.mainColor}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAddressSelection(address.id)}
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{address.name}</p>
                        <p className="text-sm">
                          {address.street} {address.buildingNumber}
                          {address.apartmentNumber &&
                            `/${address.apartmentNumber}`}
                        </p>
                        <p className="text-sm">
                          {address.postalCode} {address.city}
                        </p>
                        <p className="text-sm">{address.province}</p>
                        {address.phone && (
                          <p className="text-sm">{address.phone}</p>
                        )}
                        {address.isDefault && (
                          <span className={`text-xs bg-${config.theme.mainColor}-100 text-blu${config.theme.mainColor}e-800 px-2 py-0.5 rounded mt-1 inline-block`}>
                            Domyślny
                          </span>
                        )}
                      </div>
                      {selectedAddressId === address.id && (
                        <div className="bg-primary text-white rounded-full p-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Używamy ukrytego pola do przekazania ID adresu */}
                <Controller
                  name="addressId"
                  control={control}
                  defaultValue={
                    !useManualAddress ? selectedAddressId || '' : ''
                  }
                  render={({ field }) => <input type="hidden" {...field} />}
                />
              </div>
            )}
          </motion.div>
        )}

      {/* Formularz ręcznego wprowadzania adresu - pokazujemy gdy nie ma zapisanych adresów lub użytkownik wybierze opcję "Wprowadź nowy adres" */}
      {(useManualAddress ||
        !currentUser ||
        !currentUser.addresses ||
        currentUser.addresses.length === 0) && (
        <motion.div variants={itemVariants}>
          <p className="text-gray-700 font-medium mb-4">Dane adresowe</p>
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <Controller
                  name="fullName"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: useManualAddress
                      ? 'To pole jest wymagane'
                      : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Imię i nazwisko"
                      error={!!errors.fullName}
                      helperText={errors.fullName?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: useManualAddress
                      ? 'To pole jest wymagane'
                      : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Telefon kontaktowy"
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>
            </div>

            <FormControl fullWidth>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                rules={{
                  required: useManualAddress ? 'E-mail jest wymagany' : false,
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: 'Niepoprawny adres email',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className="bg-gray-50"
                  />
                )}
              />
            </FormControl>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <Controller
                  name="street"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: useManualAddress
                      ? 'To pole jest wymagane'
                      : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Ulica"
                      error={!!errors.street}
                      helperText={errors.street?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>

              <div className="grid grid-cols-2 gap-2">
                <FormControl fullWidth>
                  <Controller
                    name="buildingNumber"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: useManualAddress
                        ? 'To pole jest wymagane'
                        : false,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nr budynku"
                        error={!!errors.buildingNumber}
                        helperText={errors.buildingNumber?.message}
                        fullWidth
                        variant="outlined"
                        size="small"
                        className="bg-gray-50"
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Controller
                    name="apartmentNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nr lokalu"
                        error={!!errors.apartmentNumber}
                        helperText={errors.apartmentNumber?.message}
                        fullWidth
                        variant="outlined"
                        size="small"
                        className="bg-gray-50"
                      />
                    )}
                  />
                </FormControl>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <Controller
                  name="postalCode"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: useManualAddress
                      ? 'To pole jest wymagane'
                      : false,
                    pattern: {
                      value: /^\d{2}-\d{3}$/,
                      message: 'Format: 00-000',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Kod pocztowy"
                      error={!!errors.postalCode}
                      helperText={errors.postalCode?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="00-000"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Controller
                  name="city"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: useManualAddress
                      ? 'To pole jest wymagane'
                      : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Miejscowość"
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>
            </div>

            <FormControl fullWidth>
              <Controller
                name="province"
                control={control}
                defaultValue=""
                rules={{
                  required: useManualAddress ? 'To pole jest wymagane' : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    error={!!errors.province}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className="bg-gray-50"
                  >
                    <MenuItem value="" disabled>
                      Wybierz województwo
                    </MenuItem>
                    {POLISH_VOIVODESHIPS.map((voivodeship) => (
                      <MenuItem key={voivodeship} value={voivodeship}>
                        {voivodeship}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.province && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.province?.message}
                </p>
              )}
            </FormControl>

            {/* Checkbox do zapisania adresu dla zalogowanych użytkowników */}
            {currentUser && useManualAddress && (
              <div className="flex items-start mt-2">
                <div className="flex items-center h-5">
                  <Controller
                    name="saveAddress"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary rounded"
                        {...field}
                      />
                    )}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label className="font-medium text-gray-700">
                    Zapisz ten adres do mojego konta
                  </label>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Dane do faktury dla firm - ZAWSZE pokazujemy, gdy wybrano firmę */}
      {isCompany && (
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <p className="text-gray-700 font-medium mb-4">Dane do faktury</p>
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <Controller
                  name="NIP"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: isCompany ? 'To pole jest wymagane' : false,
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'NIP musi mieć 10 cyfr',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="NIP"
                      error={!!errors.NIP}
                      helperText={errors.NIP?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Controller
                  name="companyName"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: isCompany ? 'To pole jest wymagane' : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nazwa firmy"
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>
            </div>

            <FormControl fullWidth>
              <Controller
                name="companyStreetAndNumber"
                control={control}
                defaultValue=""
                rules={{
                  required: isCompany ? 'To pole jest wymagane' : false,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ulica i numer"
                    error={!!errors.companyStreetAndNumber}
                    helperText={errors.companyStreetAndNumber?.message}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className="bg-gray-50"
                  />
                )}
              />
            </FormControl>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl fullWidth>
                <Controller
                  name="companyPostalCode"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: isCompany ? 'To pole jest wymagane' : false,
                    pattern: {
                      value: /^\d{2}-\d{3}$/,
                      message: 'Format: 00-000',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Kod pocztowy"
                      error={!!errors.companyPostalCode}
                      helperText={errors.companyPostalCode?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="00-000"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <Controller
                  name="companyCity"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: isCompany ? 'To pole jest wymagane' : false,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Miejscowość"
                      error={!!errors.companyCity}
                      helperText={errors.companyCity?.message}
                      fullWidth
                      variant="outlined"
                      size="small"
                      className="bg-gray-50"
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AddressForm;
